import React, { useState, useEffect } from 'react';
import {
	Box,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Button,
	FormControl,
	FormLabel,
	Input,
	InputGroup,
	InputRightElement,
	Select,
	Switch,
	Spinner,
	FormErrorMessage,
	useToast,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
} from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import { Select as ChakraReactSelect } from 'chakra-react-select';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import { Countries } from '../remote_config/enums';
import { UserOverride } from '@repo/alictus-common/types/user';
import { PlayerSegment } from '@repo/alictus-common/types/segment';
import { Platform } from '@repo/alictus-common/enums/platform';
import userOverrideSchema from '@repo/alictus-common/validation/user_override_schema';
import axios from 'axios';
import config from '../config';
import globalStore from '../store';
import { set } from 'lodash';

interface UserOverrideModalProps {
	isOpen: boolean;
	onClose: () => void;
}

type ABTestMinimal = { uid: string; name: string };

// Generate country list for dropdown but discard if country name is "All"
const countryList = Countries.filter((country) => country.code !== 'All').map((country) => ({ value: country.code, label: country.name }));

const UserOverrideModal: React.FC<UserOverrideModalProps> = ({ isOpen, onClose }) => {
	const toast = useToast();
	let globalData = globalStore();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [userOverrides, setUserOverrides] = useState<UserOverride[] | null>(null);
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [manualInput, setManualInput] = useState<string>('');
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [isOverrideActive, setIsOverrideActive] = useState<boolean>(false);
	const [segments, setSegments] = useState<PlayerSegment[]>([]);
	const [selectedIncludedSegments, setSelectedIncludedSegments] = useState<string[]>([]);
	const [selectedExcludedSegments, setSelectedExcludedSegments] = useState<string[]>([]);
	const [abTests, setAbTests] = useState<ABTestMinimal[]>([]);
	const [selectedIncludedABTests, setSelectedIncludedABTests] = useState<string[]>([]);
	const [selectedExcludedABTests, setSelectedExcludedABTests] = useState<string[]>([]);

	const [formData, setFormData] = useState<UserOverride>({
		id: 0,
		userId: 0,
		activateOverride: true,
		platform: Platform.iOS,
		deviceId: null,
		country: null,
		time: null,
		isAlictusUser: null,
		playfabId: null,
		newUser: null,
		includedSegmentNames: null,
		excludedSegmentNames: null,
		includedABTestUids: null,
		excludedABTestUids: null,
	});

	useEffect(() => {
		const fetchUserOverride = async () => {
			try {
				const [userOverrideResponse, segmentsResponse, abTestsResponse] = await Promise.all([
					axios.get(`${config.API_ENDPOINT}/game/` + globalData.gameId + `/user_override`, {
						headers: { Authorization: 'Bearer ' + globalData.JWTToken },
					}),
					axios.get(`${config.API_ENDPOINT}/game/${globalData.gameId}/segment`, {
						headers: { Authorization: 'Bearer ' + globalData.JWTToken },
					}),
					axios.get(`${config.API_ENDPOINT}/game/${globalData.gameId}/ab_tests`, {
						headers: { Authorization: 'Bearer ' + globalData.JWTToken },
					}),
				]);

				const fetchedData: UserOverride[] = userOverrideResponse.data;
				setUserOverrides(fetchedData);

				const segmentsData: PlayerSegment[] = segmentsResponse.data;
				setSegments(segmentsData);

				const abTestsData: ABTestMinimal[] = abTestsResponse.data;
				setAbTests(abTestsData);
			} catch (err) {
				console.error('An error occurred while fetching data:', err);
			} finally {
				setIsLoading(false);
			}
		};

		if (isOpen) {
			fetchUserOverride();
		}
	}, [isOpen, globalData.gameId, globalData.JWTToken]);

	useEffect(() => {
		if (isOpen && userOverrides) {
			const ios = userOverrides.find((override) => override.platform === Platform.iOS);
			const android = userOverrides.find((override) => override.platform === Platform.Android);

			// Helper function to set form data for a platform
			const setFormDataForPlatform = (platformData: UserOverride) => {
				setFormData(platformData);
				setIsOverrideActive(platformData.activateOverride);
				setManualInput(platformData.time ? platformData.time.toString() : '');

				// Set selected segments based on form data
				setSelectedIncludedSegments(
					platformData.includedSegmentNames ? platformData.includedSegmentNames.split(',').map((s) => s.trim()) : [],
				);
				setSelectedExcludedSegments(
					platformData.excludedSegmentNames ? platformData.excludedSegmentNames.split(',').map((s) => s.trim()) : [],
				);
				setSelectedIncludedABTests(platformData.includedABTestUids ? platformData.includedABTestUids.split(',').map((s) => s.trim()) : []);
				setSelectedExcludedABTests(platformData.excludedABTestUids ? platformData.excludedABTestUids.split(',').map((s) => s.trim()) : []);
			};

			// Helper function to reset form data
			const resetFormData = () => {
				setFormData({
					id: 0,
					userId: 0,
					activateOverride: false,
					platform: Platform.iOS,
					deviceId: null,
					country: null,
					time: null,
					isAlictusUser: null,
					playfabId: null,
					newUser: null,
					includedSegmentNames: null,
					excludedSegmentNames: null,
					includedABTestUids: null,
					excludedABTestUids: null,
				});
				setIsOverrideActive(false);
				setManualInput('');
				setSelectedIncludedSegments([]);
				setSelectedExcludedSegments([]);
				setSelectedIncludedABTests([]);
				setSelectedExcludedABTests([]);
			};

			// Check if only iOS is active
			if (ios && ios.activateOverride && (!android || !android.activateOverride)) {
				setFormDataForPlatform(ios);
			}
			// Check if only Android is active
			else if (android && android.activateOverride && (!ios || !ios.activateOverride)) {
				setFormDataForPlatform(android);
			}
			// If both are active or both are inactive, show iOS data if it exists
			else if (ios) {
				setFormDataForPlatform(ios);
			}
			// If iOS doesn't exist but Android does, show Android data
			else if (android) {
				setFormDataForPlatform(android);
			}
			// If neither exists, reset to default
			else {
				resetFormData();
			}
		}
	}, [isOpen, userOverrides]);

	// Removed formData.platform dependency

	// Handle input changes and update state
	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		const newValue = name === 'platform' ? Number(value) : value;

		if (name === 'platform') {
			// When platform changes, check if data exists for it
			const platformData = userOverrides?.find((data) => data.platform === Number(value));
			if (platformData) {
				setFormData(platformData);
				setManualInput(platformData.time ? platformData.time.toString() : '');
				setIsOverrideActive(platformData.activateOverride);

				// Set selected segments based on form data
				setSelectedIncludedSegments(
					platformData.includedSegmentNames ? platformData.includedSegmentNames.split(',').map((s) => s.trim()) : [],
				);
				setSelectedExcludedSegments(
					platformData.excludedSegmentNames ? platformData.excludedSegmentNames.split(',').map((s) => s.trim()) : [],
				);
				setSelectedIncludedABTests(platformData.includedABTestUids ? platformData.includedABTestUids.split(',').map((s) => s.trim()) : []);
				setSelectedExcludedABTests(platformData.excludedABTestUids ? platformData.excludedABTestUids.split(',').map((s) => s.trim()) : []);
			} else {
				// Reset form if no data exists for the selected platform
				setFormData({
					id: 0,
					userId: 0,
					activateOverride: false,
					platform: Number(value),
					deviceId: null,
					country: null,
					time: null,
					isAlictusUser: null,
					playfabId: null,
					newUser: null,
					includedSegmentNames: null,
					excludedSegmentNames: null,
					includedABTestUids: null,
					excludedABTestUids: null,
				});
				setManualInput('');
				setIsOverrideActive(false);
				setSelectedIncludedSegments([]);
				setSelectedExcludedSegments([]);
				setSelectedIncludedABTests([]);
				setSelectedExcludedABTests([]);
			}
		} else {
			setFormData((prev) => ({ ...prev, [name]: newValue }));
		}
	};

	const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setManualInput(value);

		if (!isNaN(Number(value)) && Number(value) >= 0) {
			const epochTime = Number(value);
			setFormData((prev) => ({ ...prev, time: epochTime }));
			setSelectedDate(new Date(epochTime * 1000)); // Sync selectedDate with manual input
		} else {
			setFormData((prev) => ({ ...prev, time: null }));
			setSelectedDate(null); // Clear selectedDate if manual input is invalid
		}
	};

	const handleDateChange = (date: Date | null) => {
		setSelectedDate(date);
		if (date) {
			const epochTime = Math.floor(date.getTime() / 1000); // Convert to seconds
			setFormData((prev) => ({ ...prev, time: epochTime }));
			setManualInput(epochTime.toString()); // Sync manual input with selected date
		} else {
			setFormData((prev) => ({ ...prev, time: null }));
		}
	};

	const handleIsAlictusUser = (value: string) => {
		const newValue = value === 'null' ? null : value === 'true';
		setFormData((prev) => ({ ...prev, isAlictusUser: newValue }));
	};

	const handleNewUserChange = (value: string) => {
		const newValue = value === 'null' ? null : value === 'true';
		setFormData((prev) => ({ ...prev, newUser: newValue }));
	};

	const handleIncludedSegmentChange = (selectedOptions: any) => {
		const selectedNames = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
		setSelectedIncludedSegments(selectedNames);
		setFormData((prev) => ({
			...prev,
			includedSegmentNames: selectedNames.length > 0 ? selectedNames.join(',') : null,
		}));
	};

	const handleExcludedSegmentChange = (selectedOptions: any) => {
		const selectedNames = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
		setSelectedExcludedSegments(selectedNames);
		console.log('setSelectedExcludedSegments', selectedNames);
		setFormData((prev) => ({
			...prev,
			excludedSegmentNames: selectedNames.length > 0 ? selectedNames.join(',') : null,
		}));
	};

	const handleIncludedABTestChange = (selectedOptions: any) => {
		const selectedUids = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
		setSelectedIncludedABTests(selectedUids);
		setFormData((prev) => ({
			...prev,
			includedABTestUids: selectedUids.length > 0 ? selectedUids.join(',') : null,
		}));
	};

	const handleExcludedABTestChange = (selectedOptions: any) => {
		const selectedUids = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
		setSelectedExcludedABTests(selectedUids);
		setFormData((prev) => ({
			...prev,
			excludedABTestUids: selectedUids.length > 0 ? selectedUids.join(',') : null,
		}));
	};

	// Validate form data on submit
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			const validatedData = userOverrideSchema.parse({
				...formData,
				time: formData.time ? Number(formData.time) : null,
			});
			console.log('Creating user override with data:', validatedData);
			const response = await axios.post(`${config.API_ENDPOINT}/game/` + globalData.gameId + `/user_override`, validatedData, {
				headers: { Authorization: 'Bearer ' + globalData.JWTToken },
			});

			if (response.status === 201) {
				openToast(
					'User Override Created Successfully',
					(formData.platform === 1 ? 'iOS' : 'Android') + ' User Override has been created successfully.',
					'success',
				);
			} else {
				openToast('Error', 'An error occurred while creating user override.', 'error');
			}
			handleClose();
		} catch (err: any) {
			if (err.issues) {
				// Collect validation errors
				const validationErrors: { [key: string]: string } = {};
				err.issues.forEach((issue: any) => {
					validationErrors[issue.path[0]] = issue.message;
				});
				setErrors(validationErrors);

				openToast('Validation Error', 'Please check the form for errors.', 'error');
			} else {
				console.error('An unexpected error occurred:', err);
				openToast('Error', 'An unexpected error occurred.', 'error');
			}
		}
	};

	const handleUpdate = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			const validatedData = userOverrideSchema.parse({
				...formData,
				time: formData.time ? Number(formData.time) : null,
			});
			console.log('Updating user override with data:', validatedData);

			const response = await axios.put(
				`${config.API_ENDPOINT}/game/` + globalData.gameId + `/user_override/${formData.id}`,
				validatedData,
				{
					headers: { Authorization: 'Bearer ' + globalData.JWTToken },
				},
			);

			if (response.status === 200) {
				openToast(
					'User Override Updated Successfully',
					(formData.platform === 1 ? 'iOS' : 'Android') + ' User Override has been updated successfully.',
					'success',
				);
			} else {
				openToast('Error', 'An error occurred while updating user override.', 'error');
			}
			handleClose();
		} catch (err: any) {
			if (err.issues) {
				// Collect validation errors
				const validationErrors: { [key: string]: string } = {};
				err.issues.forEach((issue: any) => {
					validationErrors[issue.path[0]] = issue.message;
				});
				setErrors(validationErrors);

				openToast('Validation Error', 'Please check the form for errors.', 'error');
			} else {
				console.error('An unexpected error occurred:', err);

				openToast('Error', 'An unexpected error occurred.', 'error');
			}
		}
	};

	const openToast = (title: string, description: string, status: 'success' | 'error') => {
		toast({
			title,
			description,
			status,
			duration: 5000,
			position: 'top',
			isClosable: true,
		});
	};

	// Reset form and close modal
	const handleClose = () => {
		setFormData({
			id: 0,
			userId: 0,
			activateOverride: false,
			platform: Platform.iOS,
			deviceId: null,
			country: null,
			time: null,
			isAlictusUser: null,
			playfabId: null,
			newUser: null,
			includedSegmentNames: null,
			excludedSegmentNames: null,
			includedABTestUids: null,
			excludedABTestUids: null,
		});
		setErrors({});
		setManualInput('');
		setSelectedDate(null);
		setSelectedIncludedSegments([]);
		setSelectedExcludedSegments([]);
		setSelectedIncludedABTests([]);
		setSelectedExcludedABTests([]);
		onClose();
	};

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="100%">
				<Spinner size="xl" />
			</Box>
		);
	}

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Manage User Override</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{/* Platform ID Input */}
					<FormControl mb={6} isInvalid={!!errors.platform}>
						<FormLabel>Platform</FormLabel>
						<Select name="platform" value={formData.platform} onChange={(e) => handleChange(e as React.ChangeEvent<HTMLSelectElement>)}>
							<option value={Platform.iOS}>iOS</option>
							<option value={Platform.Android}>Android</option>
						</Select>
						{errors.platform && <FormErrorMessage>{errors.platform}</FormErrorMessage>}
					</FormControl>
					<Tabs>
						<form>
							<TabList>
								<Tab>General</Tab>
								<Tab>Segments</Tab>
								<Tab>AB Tests</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									{/* Playfab ID Input */}
									<FormControl mb={4} isInvalid={!!errors.playfabId} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Playfab ID</FormLabel>
										<Input
											name="playfabId"
											value={formData.playfabId || ''}
											onChange={handleChange}
											placeholder="Enter Playfab ID"
											isDisabled={!isOverrideActive}
										/>
										{errors.playfabId && <FormErrorMessage>{errors.playfabId}</FormErrorMessage>}
									</FormControl>

									{/* Device ID Input */}
									<FormControl mb={4} isInvalid={!!errors.deviceId} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Device ID</FormLabel>
										<Input
											name="deviceId"
											value={formData.deviceId || ''}
											onChange={handleChange}
											placeholder="Enter Device ID"
											isDisabled={!isOverrideActive}
										/>
										{errors.deviceId && <FormErrorMessage>{errors.deviceId}</FormErrorMessage>}
									</FormControl>

									{/* Country Select */}
									<FormControl mb={4} isInvalid={!!errors.country} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Country</FormLabel>
										<ChakraReactSelect
											options={[{ label: 'Select Country (null)', value: null }, ...countryList]}
											isMulti={false}
											placeholder="Select Country"
											value={
												formData.country
													? countryList.find((country) => country.value === formData.country)
													: { label: 'Select Country (null)', value: null }
											}
											onChange={(selectedOption) => setFormData((prev) => ({ ...prev, country: selectedOption?.value || null }))}
											isDisabled={!isOverrideActive}
										/>
										{errors.country && <FormErrorMessage>{errors.country}</FormErrorMessage>}
									</FormControl>

									{/* Time Input Field */}
									<FormControl mb={4} isInvalid={!!errors.time} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Time (Epoch)</FormLabel>
										<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
											<InputGroup>
												<Input
													type="number"
													step="1"
													value={manualInput}
													onChange={handleManualInputChange}
													placeholder="Enter epoch time"
													isDisabled={!isOverrideActive}
												/>
												<InputRightElement opacity={0.5} pointerEvents="none" minW={180}>
													{manualInput && !isNaN(Number(manualInput)) && Number(manualInput) > 0
														? new Date(Number(manualInput) * 1000).toLocaleString('tr-TR')
														: ''}
												</InputRightElement>
											</InputGroup>
											<DatePicker
												selected={selectedDate}
												onChange={handleDateChange}
												showTimeSelect
												timeIntervals={1}
												customInput={
													<Button size="sm" colorScheme="blue" leftIcon={<CalendarIcon />} isDisabled={!formData.activateOverride}>
														Pick
													</Button>
												}
											/>
										</div>
										{errors.time && <FormErrorMessage>{errors.time}</FormErrorMessage>}
									</FormControl>

									{/* Is Alictus User Dropdown */}
									<FormControl mb={4} isInvalid={!!errors.isAlictusUser} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Is Alictus User</FormLabel>
										<Select
											value={formData.isAlictusUser === null ? 'null' : String(formData.isAlictusUser)}
											onChange={(e) => handleIsAlictusUser(e.target.value)}
											isDisabled={!isOverrideActive}
										>
											<option value="null">Select User Status</option>
											<option value="true">Yes</option>
											<option value="false">No</option>
										</Select>
										{errors.isAlictusUser && <FormErrorMessage>{errors.isAlictusUser}</FormErrorMessage>}
									</FormControl>

									{/* New User Dropdown */}
									<FormControl mb={4} isInvalid={!!errors.newUser} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>New User</FormLabel>
										<Select
											value={formData.newUser === null ? 'null' : String(formData.newUser)}
											onChange={(e) => handleNewUserChange(e.target.value)}
											isDisabled={!isOverrideActive}
										>
											<option value="null">Select New User</option>
											<option value="true">Yes</option>
											<option value="false">No</option>
										</Select>
										{errors.newUser && <FormErrorMessage>{errors.newUser}</FormErrorMessage>}
									</FormControl>
								</TabPanel>
								<TabPanel>
									{/* Included Segments Dropdown */}
									<FormControl mb={4} isInvalid={!!errors.includedSegmentNames} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Included Segments</FormLabel>
										<ChakraReactSelect
											isMulti
											placeholder="Select included segments..."
											options={segments
												.filter((segment) => !selectedExcludedSegments.includes(segment.name))
												.map((segment) => ({ value: segment.name, label: segment.name }))}
											value={segments
												.filter((segment) => selectedIncludedSegments.includes(segment.name))
												.map((segment) => ({ value: segment.name, label: segment.name }))}
											onChange={handleIncludedSegmentChange}
											isDisabled={!isOverrideActive || selectedExcludedSegments.length > 0}
											closeMenuOnSelect={false}
											hideSelectedOptions={false}
											components={{
												Option: ({ children, ...props }) => (
													<div
														{...props.innerProps}
														style={{
															display: 'flex',
															alignItems: 'center',
															padding: '8px 12px',
															cursor: 'pointer',
															backgroundColor: props.isSelected ? '#EBF8FF' : props.isFocused ? '#F7FAFC' : 'white',
														}}
													>
														<input
															type="checkbox"
															checked={props.isSelected}
															onChange={() => {}}
															style={{ marginRight: '8px', accentColor: '#3182CE' }}
														/>
														<span style={{ color: props.isSelected ? '#2B6CB0' : '#2D3748' }}>{children}</span>
													</div>
												),
											}}
											chakraStyles={{
												control: (provided, state) => ({
													...provided,
													boxShadow: 'none !important',
													borderColor: state.isFocused ? 'gray.500 !important' : '#ffffff29 !important',
													outline: 'none !important',
												}),
												multiValue: (provided) => ({
													...provided,
													bg: 'blue.300',
												}),
												multiValueLabel: (provided) => ({
													...provided,
													color: 'blue.800',
												}),
											}}
										/>
										{errors.includedSegmentNames && <FormErrorMessage>{errors.includedSegmentNames}</FormErrorMessage>}
									</FormControl>

									{/* Excluded Segments Dropdown */}
									<FormControl mb={4} isInvalid={!!errors.excludedSegmentNames} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Excluded Segments</FormLabel>
										<ChakraReactSelect
											isMulti
											placeholder="Select excluded segments..."
											options={segments
												.filter((segment) => !selectedIncludedSegments.includes(segment.name))
												.map((segment) => ({ value: segment.name, label: segment.name }))}
											value={segments
												.filter((segment) => selectedExcludedSegments.includes(segment.name))
												.map((segment) => ({ value: segment.name, label: segment.name }))}
											onChange={handleExcludedSegmentChange}
											isDisabled={!isOverrideActive || selectedIncludedSegments.length > 0}
											closeMenuOnSelect={false}
											hideSelectedOptions={false}
											components={{
												Option: ({ children, ...props }) => (
													<div
														{...props.innerProps}
														style={{
															display: 'flex',
															alignItems: 'center',
															padding: '8px 12px',
															cursor: 'pointer',
															backgroundColor: props.isSelected ? '#FED7D7' : props.isFocused ? '#F7FAFC' : 'white',
														}}
													>
														<input
															type="checkbox"
															checked={props.isSelected}
															onChange={() => {}}
															style={{ marginRight: '8px', accentColor: '#E53E3E' }}
														/>
														<span style={{ color: props.isSelected ? '#C53030' : '#2D3748' }}>{children}</span>
													</div>
												),
											}}
											chakraStyles={{
												control: (provided, state) => ({
													...provided,
													boxShadow: 'none !important',
													borderColor: state.isFocused ? 'gray.500 !important' : '#ffffff29 !important',
													outline: 'none !important',
												}),
												multiValue: (provided) => ({
													...provided,
													bg: 'red.300',
												}),
												multiValueLabel: (provided) => ({
													...provided,
													color: 'red.800',
												}),
											}}
										/>
										{errors.excludedSegmentNames && <FormErrorMessage>{errors.excludedSegmentNames}</FormErrorMessage>}
									</FormControl>
								</TabPanel>
								<TabPanel>
									<FormControl mb={4} isInvalid={!!errors.includedABTestUids} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Included AB Tests</FormLabel>
										<ChakraReactSelect
											isMulti
											placeholder="Select included AB tests..."
											options={abTests
												.filter((ab) => !selectedExcludedABTests.includes(ab.uid))
												.map((ab) => ({ value: ab.uid, label: ab.name }))}
											value={abTests
												.filter((ab) => selectedIncludedABTests.includes(ab.uid))
												.map((ab) => ({ value: ab.uid, label: ab.name }))}
											onChange={handleIncludedABTestChange}
											isDisabled={!isOverrideActive || selectedExcludedABTests.length > 0}
											closeMenuOnSelect={false}
											hideSelectedOptions={false}
											components={{
												Option: ({ children, ...props }) => (
													<div
														{...props.innerProps}
														style={{
															display: 'flex',
															alignItems: 'center',
															padding: '8px 12px',
															cursor: 'pointer',
															backgroundColor: props.isSelected ? '#EBF8FF' : props.isFocused ? '#F7FAFC' : 'white',
														}}
													>
														<input
															type="checkbox"
															checked={props.isSelected}
															onChange={() => {}}
															style={{ marginRight: '8px', accentColor: '#3182CE' }}
														/>
														<span style={{ color: props.isSelected ? '#2B6CB0' : '#2D3748' }}>{children}</span>
													</div>
												),
											}}
											chakraStyles={{
												control: (provided, state) => ({
													...provided,
													boxShadow: 'none !important',
													borderColor: state.isFocused ? 'gray.500 !important' : '#ffffff29 !important',
													outline: 'none !important',
												}),
												multiValue: (provided) => ({
													...provided,
													bg: 'blue.300',
												}),
												multiValueLabel: (provided) => ({
													...provided,
													color: 'blue.800',
												}),
											}}
										/>
										{errors.includedABTestUids && <FormErrorMessage>{errors.includedABTestUids}</FormErrorMessage>}
									</FormControl>

									<FormControl mb={4} isInvalid={!!errors.excludedABTestUids} opacity={isOverrideActive ? 1 : 0.5}>
										<FormLabel>Excluded AB Tests</FormLabel>
										<ChakraReactSelect
											isMulti
											placeholder="Select excluded AB tests..."
											options={abTests
												.filter((ab) => !selectedIncludedABTests.includes(ab.uid))
												.map((ab) => ({ value: ab.uid, label: ab.name }))}
											value={abTests
												.filter((ab) => selectedExcludedABTests.includes(ab.uid))
												.map((ab) => ({ value: ab.uid, label: ab.name }))}
											onChange={handleExcludedABTestChange}
											isDisabled={!isOverrideActive || selectedIncludedABTests.length > 0}
											closeMenuOnSelect={false}
											hideSelectedOptions={false}
											components={{
												Option: ({ children, ...props }) => (
													<div
														{...props.innerProps}
														style={{
															display: 'flex',
															alignItems: 'center',
															padding: '8px 12px',
															cursor: 'pointer',
															backgroundColor: props.isSelected ? '#FED7D7' : props.isFocused ? '#F7FAFC' : 'white',
														}}
													>
														<input
															type="checkbox"
															checked={props.isSelected}
															onChange={() => {}}
															style={{ marginRight: '8px', accentColor: '#E53E3E' }}
														/>
														<span style={{ color: props.isSelected ? '#C53030' : '#2D3748' }}>{children}</span>
													</div>
												),
											}}
											chakraStyles={{
												control: (provided, state) => ({
													...provided,
													boxShadow: 'none !important',
													borderColor: state.isFocused ? 'gray.500 !important' : '#ffffff29 !important',
													outline: 'none !important',
												}),
												multiValue: (provided) => ({
													...provided,
													bg: 'red.300',
												}),
												multiValueLabel: (provided) => ({
													...provided,
													color: 'red.800',
												}),
											}}
										/>

										{errors.excludedABTestUids && <FormErrorMessage>{errors.excludedABTestUids}</FormErrorMessage>}
									</FormControl>
								</TabPanel>


							</TabPanels>

															{/* Activate Override Switch */}
								<FormControl mb={4} display="flex" alignItems="center" isInvalid={!!errors.activateOverride}>
									<FormLabel mb={0}>Activate Override</FormLabel>
									<Switch
										name="activateOverride"
										isChecked={isOverrideActive}
										onChange={() => {
											const newValue = !isOverrideActive;
											setIsOverrideActive(newValue);
											setFormData((prev) => ({ ...prev, activateOverride: newValue }));
										}}
									/>
									{errors.activateOverride && <FormErrorMessage>{errors.activateOverride}</FormErrorMessage>}
								</FormControl>

								{/* Submit Button */}
								<ModalFooter>
									{userOverrides?.some((override) => override.platform === formData.platform) ? (
										<Button type="button" colorScheme="blue" mr={3} onClick={handleUpdate}>
											Update
										</Button>
									) : (
										<Button type="submit" colorScheme="blue" mr={3} onClick={handleSubmit}>
											Submit
										</Button>
									)}
									<Button onClick={handleClose}>Cancel</Button>
								</ModalFooter>
						</form>
					</Tabs>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

export default UserOverrideModal;
